import { type ReactElement, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { NotFoundScreen } from '@/pages/NotFoundScreen';
import { useNotifications } from '@/providers/notificationHooks';
import { useProjectContextGuard } from '@/shared/hooks/useProjectContextGuard';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { useDeleteRelease, useReleaseDetailQuery } from '../hooks/releases';
import { ReleaseDetailContent } from './ReleaseDetailContent';
import type { AuthSession } from '@/auth/types';

type ReleaseDetailPageProps = { session: AuthSession | null };

export const ReleaseDetailPage = ({ session }: ReleaseDetailPageProps): ReactElement => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const deleteRelease = useDeleteRelease();
  const searchSlug = useMemo(() => (slug ?? '').trim(), [slug]);

  const releaseQuery = useReleaseDetailQuery(searchSlug || null, selectedProjectId ?? null);
  const {
    data: release,
    isPending: isReleasePending,
    isError: isReleaseError,
    error,
  } = releaseQuery;

  useProjectContextGuard({
    entity: release,
    isPending: isReleasePending,
    isFetched: releaseQuery.isFetched,
    selectedProjectId,
    setSelectedProjectId,
    syncContextOnMismatch: true,
    listPath: '/releases',
  });
  const showNotFound = !isReleasePending && releaseQuery.isFetched && !release;

  const handleDeleteRelease = async (): Promise<void> => {
    if (!release) return;
    try {
      await deleteRelease.mutateAsync({ id: release.id, projectId: release.projectId });
      notify({ message: 'Release deleted.', tone: 'green' });
      void navigate('/releases', { replace: true });
    } catch (deleteError) {
      const message =
        deleteError instanceof Error ? deleteError.message : 'Failed to delete release.';
      notify({ message, tone: 'red' });
    }
  };

  if (showNotFound) {
    return (
      <NotFoundScreen
        title="Release not found"
        body="This release does not exist or you no longer have access to it."
        backTo="/releases"
        backLabel="Back to releases"
      />
    );
  }

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view release details."
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      isPending={isReleasePending}
      isError={isReleaseError}
      error={error}
      errorMessage="Failed to load release"
      data={release}
      loadingFallback={<></>}
    >
      {(resolvedRelease) => (
        <ReleaseDetailContent
          title={resolvedRelease.name}
          tags={resolvedRelease.tags}
          onDelete={handleDeleteRelease}
          deleteLoading={deleteRelease.isPending}
        />
      )}
    </DetailPageScaffold>
  );
};
